import { useCallback, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "./components/Layout";
import { ConnectionPill } from "./components/ConnectionPill";
import { TritonChat } from "./surfaces/TritonChat";
import { TritonChatScreen } from "./surfaces/TritonChatScreen";
import { AlexandriaStore } from "./surfaces/AlexandriaStore";
import { ManageSurface } from "./surfaces/ManageSurface";
import { SettingsSurface } from "./surfaces/SettingsSurface";
import { TwinAboutMe } from "./surfaces/TwinAboutMe";
import { TwinProfileEditor } from "./surfaces/TwinProfileEditor";
import { ColdStartOnboarding } from "./surfaces/ColdStartOnboarding";
import { DemoScreen } from "./surfaces/DemoScreen";
import { MemoriesSurface } from "./surfaces/MemoriesSurface";
import { useAuth } from "./lib/auth";
import { useAideSocket } from "./lib/useAideSocket";
import type { EnvelopeFromServer } from "./lib/aide-protocol";


export default function App() {
  return (
    <Routes>
      {/* Corp demo surface: scripted carrier-swappable demos for
          SoftBank/PayPay + KDDI/au+Coincheck prospect calls. Mounted
          outside the ConsumerApp shell so it does NOT open the Aide
          WebSocket (the demo replays scripts locally, no backend). */}
      <Route path="demo" element={<DemoScreen />} />
      <Route path="*" element={<ConsumerApp />} />
    </Routes>
  );
}


// Everything below this point is the consumer-facing product shell.
// It opens one Aide WebSocket per consumer per process and routes the
// chat / store / settings surfaces (per LS-SPEC-22 §Consumer Surfaces).
function ConsumerApp() {
  const consumerId = useAuth((s) => s.consumerId);
  const [envelopes, setEnvelopes] = useState<EnvelopeFromServer[]>([]);
  const [pendingAttachmentCount, setPendingAttachmentCount] = useState(0);

  const onEnvelope = useCallback((env: EnvelopeFromServer) => {
    setEnvelopes((prev) => [...prev, env]);
    if (env.type === "attachment_acknowledged") {
      setPendingAttachmentCount(env.pending_count);
    }
    if (env.type === "assistant_message" || env.type === "request_approval") {
      // Pending attachments were consumed by the user_message that
      // produced this response — reset the counter.
      setPendingAttachmentCount(0);
    }
  }, []);

  const { status, send } = useAideSocket({
    consumerId,
    onEnvelope,
  });

  return (
    <Routes>
      <Route element={<Layout connectionPill={<ConnectionPill status={status} />} />}>
        {/* /triton renders the legacy chat-only surface (still used
            on small screens). /triton/graph renders the new chat +
            agent-graph layout for UC-12. */}
        <Route
          path="triton"
          element={
            <TritonChat
              status={status}
              send={send}
              envelopes={envelopes}
              pendingAttachmentCount={pendingAttachmentCount}
            />
          }
        />
        <Route
          path="triton/graph"
          element={
            <TritonChatScreen
              consumerId={consumerId}
              status={status}
              send={send}
              envelopes={envelopes}
              pendingAttachmentCount={pendingAttachmentCount}
            />
          }
        />
        <Route
          path="alexandria"
          element={
            <AlexandriaStore
              wsStatus={status}
              send={send}
              envelopes={envelopes}
            />
          }
        />
        <Route path="manage" element={<ManageSurface />} />
        <Route path="memories" element={<MemoriesSurface />} />
        <Route path="settings" element={<SettingsSurface />} />
        <Route path="about-me" element={<TwinAboutMe />} />
        <Route path="profile" element={<TwinProfileEditor />} />
        <Route path="onboard/styles" element={<ColdStartOnboarding />} />
        {/* Relative target keeps navigation within /app/lifestyle/ basename
            instead of jumping to /triton at site root. */}
        <Route path="*" element={<Navigate to="triton" replace />} />
      </Route>
    </Routes>
  );
}
